import React, { useState, useEffect } from 'react';
import { 
    Plus, 
    Edit, 
    Trash2, 
    Eye, 
    EyeOff, 
    Save, 
    X, 
    Star,
    Clock,
    ChevronDown,
    ChevronUp,
    ExternalLink,
    Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = [
    { id: 'adhd-basics', label: 'ADHD Basics' },
    { id: 'strategies', label: 'Strategies & Tools' },
    { id: 'mental-health', label: 'Mental Health' },
    { id: 'relationships', label: 'Relationships' },
    { id: 'hypnotherapy', label: 'Hypnotherapy' },
    { id: 'general', label: 'General' }
];

const ICONS = ['Brain', 'Heart', 'Shield', 'Sparkles', 'Lightbulb', 'Users', 'Zap', 'Moon', 'BookOpen'];

const BlogTab = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingPost, setEditingPost] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [expandedPost, setExpandedPost] = useState(null);

    const emptyPost = {
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        category: 'general',
        tags: [],
        author: 'CoRegulate Team',
        is_published: false,
        is_featured: false,
        meta_title: '',
        meta_description: '',
        icon: 'BookOpen'
    };

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = async () => {
        try {
            const response = await fetch(`${API_URL}/api/blog/admin/posts`);
            if (response.ok) {
                const data = await response.json();
                setPosts(data.posts || []);
            }
        } catch (error) {
            console.error('Failed to fetch posts:', error);
            toast.error('Failed to load blog posts');
        } finally {
            setLoading(false);
        }
    };

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    };

    const handleSave = async (post) => {
        try {
            const isNew = !post._id;
            const url = isNew 
                ? `${API_URL}/api/blog/admin/posts`
                : `${API_URL}/api/blog/admin/posts/${post._id}`;
            
            const method = isNew ? 'POST' : 'PUT';
            
            // Auto-generate slug if empty
            if (!post.slug && post.title) {
                post.slug = generateSlug(post.title);
            }

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(post)
            });

            if (response.ok) {
                toast.success(isNew ? 'Post created!' : 'Post updated!');
                fetchPosts();
                setEditingPost(null);
                setIsCreating(false);
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            console.error('Save failed:', error);
            toast.error('Failed to save post');
        }
    };

    const handleDelete = async (postId) => {
        if (!window.confirm('Are you sure you want to delete this post?')) return;

        try {
            const response = await fetch(`${API_URL}/api/blog/admin/posts/${postId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                toast.success('Post deleted');
                fetchPosts();
            }
        } catch (error) {
            console.error('Delete failed:', error);
            toast.error('Failed to delete post');
        }
    };

    const togglePublish = async (post) => {
        await handleSave({ ...post, is_published: !post.is_published });
    };

    const toggleFeatured = async (post) => {
        await handleSave({ ...post, is_featured: !post.is_featured });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-[#1D6B6B] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-[#1D6B6B]">Blog Posts</h2>
                    <p className="text-sm text-[#7D8E95]">
                        Manage your public blog content for SEO
                    </p>
                </div>
                <button
                    onClick={() => {
                        setIsCreating(true);
                        setEditingPost({ ...emptyPost });
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-[#1D6B6B] text-white rounded-lg hover:bg-[#145555] transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    New Post
                </button>
            </div>

            {/* Post Editor Modal */}
            {(editingPost || isCreating) && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-[#1D6B6B]">
                                {isCreating ? 'Create New Post' : 'Edit Post'}
                            </h3>
                            <button
                                onClick={() => {
                                    setEditingPost(null);
                                    setIsCreating(false);
                                }}
                                className="p-2 text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                            <PostEditor 
                                post={editingPost} 
                                onChange={setEditingPost}
                                onSave={() => handleSave(editingPost)}
                                onCancel={() => {
                                    setEditingPost(null);
                                    setIsCreating(false);
                                }}
                                generateSlug={generateSlug}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Posts List */}
            <div className="space-y-3">
                {posts.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-xl">
                        <p className="text-[#7D8E95]">No blog posts yet. Create your first post!</p>
                    </div>
                ) : (
                    posts.map((post) => (
                        <div 
                            key={post._id}
                            className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                        >
                            {/* Post Header */}
                            <div 
                                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                                onClick={() => setExpandedPost(expandedPost === post._id ? null : post._id)}
                            >
                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        {post.is_published ? (
                                            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                                                Published
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                                                Draft
                                            </span>
                                        )}
                                        {post.is_featured && (
                                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-medium text-[#1D6B6B] truncate">{post.title}</h4>
                                        <p className="text-sm text-[#7D8E95] truncate">{post.excerpt}</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-2 ml-4">
                                    <span className="text-xs text-[#7D8E95] capitalize">{post.category?.replace('-', ' ')}</span>
                                    {expandedPost === post._id ? (
                                        <ChevronUp className="w-5 h-5 text-gray-400" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5 text-gray-400" />
                                    )}
                                </div>
                            </div>

                            {/* Expanded Actions */}
                            {expandedPost === post._id && (
                                <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4 text-sm text-[#7D8E95]">
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-4 h-4" />
                                                {post.reading_time || '?'} min read
                                            </span>
                                            {post.tags?.length > 0 && (
                                                <span>Tags: {post.tags.join(', ')}</span>
                                            )}
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            {post.is_published && (
                                                <a
                                                    href={`/blog/${post.slug}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="p-2 text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-white rounded-lg transition-colors"
                                                    title="View post"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <ExternalLink className="w-4 h-4" />
                                                </a>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleFeatured(post);
                                                }}
                                                className={`p-2 rounded-lg transition-colors ${
                                                    post.is_featured 
                                                        ? 'text-yellow-500 bg-yellow-50 hover:bg-yellow-100' 
                                                        : 'text-[#7D8E95] hover:text-yellow-500 hover:bg-white'
                                                }`}
                                                title={post.is_featured ? 'Remove from featured' : 'Mark as featured'}
                                            >
                                                <Star className={`w-4 h-4 ${post.is_featured ? 'fill-yellow-500' : ''}`} />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    togglePublish(post);
                                                }}
                                                className={`p-2 rounded-lg transition-colors ${
                                                    post.is_published 
                                                        ? 'text-green-600 bg-green-50 hover:bg-green-100' 
                                                        : 'text-[#7D8E95] hover:text-green-600 hover:bg-white'
                                                }`}
                                                title={post.is_published ? 'Unpublish' : 'Publish'}
                                            >
                                                {post.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingPost(post);
                                                }}
                                                className="p-2 text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-white rounded-lg transition-colors"
                                                title="Edit"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(post._id);
                                                }}
                                                className="p-2 text-[#7D8E95] hover:text-red-600 hover:bg-white rounded-lg transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

// Post Editor Component
const PostEditor = ({ post, onChange, onSave, onCancel, generateSlug }) => {
    const [tagInput, setTagInput] = useState('');

    const updateField = (field, value) => {
        onChange({ ...post, [field]: value });
    };

    const addTag = () => {
        if (tagInput.trim() && !post.tags?.includes(tagInput.trim())) {
            updateField('tags', [...(post.tags || []), tagInput.trim()]);
            setTagInput('');
        }
    };

    const removeTag = (tag) => {
        updateField('tags', post.tags?.filter(t => t !== tag) || []);
    };

    return (
        <div className="space-y-6">
            {/* Title & Slug */}
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Title *</label>
                    <input
                        type="text"
                        value={post.title || ''}
                        onChange={(e) => updateField('title', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                        placeholder="Post title"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                        Slug 
                        <button 
                            type="button"
                            onClick={() => updateField('slug', generateSlug(post.title || ''))}
                            className="ml-2 text-xs text-[#7D8E95] hover:text-[#1D6B6B]"
                        >
                            (Auto-generate)
                        </button>
                    </label>
                    <input
                        type="text"
                        value={post.slug || ''}
                        onChange={(e) => updateField('slug', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                        placeholder="url-friendly-slug"
                    />
                </div>
            </div>

            {/* Excerpt */}
            <div>
                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Excerpt *</label>
                <textarea
                    value={post.excerpt || ''}
                    onChange={(e) => updateField('excerpt', e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30 resize-none"
                    placeholder="Brief summary for previews and SEO"
                />
            </div>

            {/* Content */}
            <div>
                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                    Content *
                    <span className="ml-2 text-xs text-[#7D8E95] font-normal">
                        (Use ## for headers, • for lists, --- for dividers)
                    </span>
                </label>
                <textarea
                    value={post.content || ''}
                    onChange={(e) => updateField('content', e.target.value)}
                    rows={12}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30 resize-none font-mono text-sm"
                    placeholder="Write your post content here..."
                />
            </div>

            {/* Category, Icon, Author */}
            <div className="grid md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Category</label>
                    <select
                        value={post.category || 'general'}
                        onChange={(e) => updateField('category', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                    >
                        {CATEGORIES.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Icon</label>
                    <select
                        value={post.icon || 'BookOpen'}
                        onChange={(e) => updateField('icon', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                    >
                        {ICONS.map(icon => (
                            <option key={icon} value={icon}>{icon}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Author</label>
                    <input
                        type="text"
                        value={post.author || ''}
                        onChange={(e) => updateField('author', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                        placeholder="Author name"
                    />
                </div>
            </div>

            {/* Tags */}
            <div>
                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Tags</label>
                <div className="flex items-center gap-2 mb-2">
                    <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                        placeholder="Add a tag"
                    />
                    <button
                        type="button"
                        onClick={addTag}
                        className="px-4 py-2 bg-gray-100 text-[#7D8E95] rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        Add
                    </button>
                </div>
                <div className="flex flex-wrap gap-2">
                    {(post.tags || []).map((tag) => (
                        <span 
                            key={tag}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-[#1D6B6B]/10 text-[#1D6B6B] text-sm rounded-full"
                        >
                            {tag}
                            <button
                                type="button"
                                onClick={() => removeTag(tag)}
                                className="hover:text-red-600"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            </div>

            {/* SEO Fields */}
            <div className="border-t border-gray-200 pt-6">
                <h4 className="text-sm font-medium text-[#1D6B6B] mb-4">SEO Settings</h4>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-[#7D8E95] mb-1">Meta Title</label>
                        <input
                            type="text"
                            value={post.meta_title || ''}
                            onChange={(e) => updateField('meta_title', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                            placeholder="SEO title (defaults to post title)"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[#7D8E95] mb-1">Meta Description</label>
                        <textarea
                            value={post.meta_description || ''}
                            onChange={(e) => updateField('meta_description', e.target.value)}
                            rows={2}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30 resize-none"
                            placeholder="SEO description (defaults to excerpt)"
                        />
                    </div>
                </div>
            </div>

            {/* Publish Settings */}
            <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={post.is_published || false}
                            onChange={(e) => updateField('is_published', e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-[#1D6B6B] focus:ring-[#1D6B6B]"
                        />
                        <span className="text-sm text-[#1D6B6B]">Publish post</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={post.is_featured || false}
                            onChange={(e) => updateField('is_featured', e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-[#1D6B6B] focus:ring-[#1D6B6B]"
                        />
                        <span className="text-sm text-[#1D6B6B]">Featured post</span>
                    </label>
                </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-6">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-6 py-2 text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={onSave}
                    className="flex items-center gap-2 px-6 py-2 bg-[#1D6B6B] text-white rounded-lg hover:bg-[#145555] transition-colors"
                >
                    <Save className="w-4 h-4" />
                    Save Post
                </button>
            </div>
        </div>
    );
};

export default BlogTab;
